import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError, request } from '@/lib/api';
import i18n from '@/lib/i18n';
import { inventoryItemSchema } from '@/lib/schemas';
import { formatNumber } from '@/lib/utils';
import { INVENTORY_QUERY_KEY } from './inventory-api';

/* ---------------------------------------------------------------------------
   יצירת פריט מלאי — מראה אחת לאחת את `CreateInventoryItemDto`.

   השדות המספריים מוחזקים כמחרוזות בטופס ומומרים בשליחה. `valueAsNumber`
   מחזיר NaN על שדה ריק, ושדה ריק כאן הוא *לא* שגיאה אלא "השאר את ברירת
   המחדל של השרת" — הפרדה שמחרוזת מבטאת ו-NaN מטשטש.
   --------------------------------------------------------------------------- */

const MAX_QTY = 1_000_000;

/** `@Matches` ב-DTO: אותיות, ספרות, נקודה, מקף וקו תחתון בלבד. */
const SKU_PATTERN = /^[A-Za-z0-9._-]+$/;

const optionalInt = (label: string) =>
  z
    .string()
    .trim()
    .refine((v) => v === '' || /^\d+$/.test(v), i18n.t('inventory:newItem.intInvalid', { label }))
    .refine((v) => v === '' || Number(v) <= MAX_QTY, i18n.t('inventory:newItem.intMax', { label, max: formatNumber(MAX_QTY) }));

const newItemSchema = z.object({
  sku: z
    .string()
    .trim()
    .min(1, i18n.t('inventory:newItem.skuRequired'))
    .max(64, i18n.t('inventory:newItem.skuMax'))
    .regex(SKU_PATTERN, i18n.t('inventory:newItem.skuPattern')),
  name: z
    .string()
    .trim()
    .min(1, i18n.t('inventory:newItem.nameRequired'))
    .max(200, i18n.t('inventory:newItem.nameMax')),
  quantity: optionalInt(i18n.t('inventory:newItem.quantity')),
  lowStockThreshold: optionalInt(i18n.t('inventory:newItem.threshold')),
  unitPrice: z
    .string()
    .trim()
    .refine(
      (v) => v === '' || /^\d+(\.\d{1,2})?$/.test(v),
      i18n.t('inventory:newItem.priceInvalid'),
    ),
  category: z.string().trim().max(100, i18n.t('inventory:newItem.categoryMax')),
});

type NewItemForm = z.infer<typeof newItemSchema>;

const blank = (v: string): string | undefined => (v === '' ? undefined : v);

export function NewItemDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['inventory', 'common']);
  const toast = useToast();
  const queryClient = useQueryClient();

  const form = useForm<NewItemForm>({
    resolver: zodResolver(newItemSchema),
    mode: 'onSubmit',
    defaultValues: {
      sku: '',
      name: '',
      quantity: '',
      lowStockThreshold: '',
      unitPrice: '',
      category: '',
    },
  });

  const { errors } = form.formState;

  const mutation = useMutation({
    mutationFn: (values: NewItemForm) => {
      const quantity = blank(values.quantity);
      const lowStockThreshold = blank(values.lowStockThreshold);
      const unitPrice = blank(values.unitPrice);
      return request('/inventory', {
        method: 'POST',
        body: {
          sku: values.sku.trim(),
          name: values.name.trim(),
          quantity: quantity === undefined ? undefined : Number(quantity),
          lowStockThreshold:
            lowStockThreshold === undefined ? undefined : Number(lowStockThreshold),
          // ה-DTO מצפה ל-number (IsNumber, maxDecimalPlaces: 2). השרת
          // עוטף אותו ב-Prisma.Decimal ומחזיר מחרוזת — הכיוון הזה הוא
          // היחיד שבו כסף עובר כמספר, וכבר אומת לשתי ספרות למעלה.
          unitPrice: unitPrice === undefined ? undefined : Number(unitPrice),
          category: blank(values.category.trim()),
        },
        schema: inventoryItemSchema,
      });
    },
    onSuccess: (item) => {
      void queryClient.invalidateQueries({ queryKey: [INVENTORY_QUERY_KEY] });
      toast.success(t('newItem.created'), `${item.name} — ${item.sku}`);
      onClose();
    },
    onError: (error: unknown) => {
      // 400 על מק"ט כפול מגיע מה-unique constraint בשרת; מצמידים אותו
      // לשדה הרלוונטי ולא רק ל-toast שנעלם.
      if (error instanceof ApiError && error.status === 400 && /sku/i.test(error.message)) {
        form.setError('sku', { type: 'server', message: t('newItem.skuExists') });
      }
      toast.error(
        t('newItem.createFailed'),
        error instanceof ApiError ? error.message : t('newItem.tryAgainShortly'),
      );
    },
  });

  const onSubmit = form.handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent aria-describedby="new-item-desc">
        <DialogHeader>
          <DialogTitle>{t('newItem.title')}</DialogTitle>
          <DialogDescription id="new-item-desc">
            {t('newItem.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <DialogBody>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={t('newItem.sku')}
                htmlFor="item-sku"
                required
                error={errors.sku?.message}
                hint={t('newItem.skuHint')}
              >
                <Input
                  id="item-sku"
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  // מק"ט הוא מזהה — נשאר LTR גם בטופס עברי.
                  className="ltr-inline text-start"
                  invalid={Boolean(errors.sku)}
                  aria-describedby={errors.sku ? 'item-sku-error' : 'item-sku-hint'}
                  {...form.register('sku')}
                />
              </Field>

              <Field
                label={t('newItem.category')}
                htmlFor="item-category"
                error={errors.category?.message}
              >
                <Input
                  id="item-category"
                  autoComplete="off"
                  invalid={Boolean(errors.category)}
                  aria-describedby={errors.category ? 'item-category-error' : undefined}
                  {...form.register('category')}
                />
              </Field>
            </div>

            <Field
              label={t('newItem.name')}
              htmlFor="item-name"
              required
              error={errors.name?.message}
            >
              <Input
                id="item-name"
                autoComplete="off"
                invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? 'item-name-error' : undefined}
                {...form.register('name')}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label={t('newItem.openingQuantity')}
                htmlFor="item-quantity"
                error={errors.quantity?.message}
                hint={t('newItem.quantityHint')}
              >
                <Input
                  id="item-quantity"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  invalid={Boolean(errors.quantity)}
                  aria-describedby={
                    errors.quantity ? 'item-quantity-error' : 'item-quantity-hint'
                  }
                  {...form.register('quantity')}
                />
              </Field>

              <Field
                label={t('newItem.threshold')}
                htmlFor="item-threshold"
                error={errors.lowStockThreshold?.message}
                hint={t('newItem.thresholdHint')}
              >
                <Input
                  id="item-threshold"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  invalid={Boolean(errors.lowStockThreshold)}
                  aria-describedby={
                    errors.lowStockThreshold ? 'item-threshold-error' : 'item-threshold-hint'
                  }
                  {...form.register('lowStockThreshold')}
                />
              </Field>

              <Field
                label={t('newItem.unitPrice')}
                htmlFor="item-price"
                error={errors.unitPrice?.message}
                hint={t('newItem.notRequired')}
              >
                <Input
                  id="item-price"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  invalid={Boolean(errors.unitPrice)}
                  aria-describedby={errors.unitPrice ? 'item-price-error' : 'item-price-hint'}
                  {...form.register('unitPrice')}
                />
              </Field>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {t('newItem.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
